import { useEffect, useMemo, useState } from 'react';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { ChartArea } from '../../components/Card';
import { useTheme } from '../../components/ThemeProvider';
import { chartColors, type ChartColors } from '../../config/chartTheme';
import {
  buildPowerLawChart,
  chartDomain,
  logDecadeTicks,
  powerLawAt,
  POWER_LAW_START_MS,
  type PricePoint,
} from '../../lib/bitcoin/powerLaw';
import type { NewhedgePowerLaw } from '../../lib/bitcoin/api';
import { formatUsd, formatUsdCompact, formatYear } from '../../lib/bitcoin/units';

/** Ticks every four years — one per halving epoch, and all that fits at 350px. */
function yearTicks(fromMs: number, toMs: number): number[] {
  const first = new Date(fromMs).getUTCFullYear();
  const last = new Date(toMs).getUTCFullYear();
  const ticks: number[] = [];
  for (let y = first; y <= last; y += 1) {
    if (y % 4 === 0) ticks.push(Date.UTC(y, 0, 1));
  }
  return ticks;
}

const SERIES_LABEL: Readonly<Record<string, string>> = {
  price: 'Price',
  fair: 'Model',
  floor: 'Floor',
};

function PowerLawTooltip({
  active,
  payload,
  label,
  colors,
}: TooltipProps<number, string> & { colors: ChartColors }) {
  if (active !== true || payload === undefined || payload.length === 0) return null;
  const t = typeof label === 'number' ? label : null;
  return (
    <div
      className="rounded-lg border border-line px-2 py-1.5 text-[10px] shadow-card"
      style={{ backgroundColor: colors.tooltipBg, color: colors.tooltipInk }}
    >
      {t !== null && <div className="mb-0.5 font-semibold">{formatYear(t)}</div>}
      {payload.map((item) => {
        const key = String(item.dataKey ?? '');
        return (
          <div key={key} className="flex items-center gap-1.5 whitespace-nowrap">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: item.color }}
              aria-hidden
            />
            <span className="opacity-70">{SERIES_LABEL[key] ?? key}</span>
            <span className="ml-auto font-mono font-semibold">
              {formatUsd(typeof item.value === 'number' ? item.value : null)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Recharts colours, re-read whenever the theme changes.
 *
 * NOT `useMemo(() => chartColors(), [theme])`, and that is deliberate.
 * `chartColors()` reads CSS custom properties off <html>, but ThemeProvider
 * flips the `dark` class in its OWN effect — and React runs a child's effects
 * (and certainly its render) before its parent's. Reading during render, or
 * synchronously in an effect here, therefore samples the OUTGOING theme's
 * values, and since the memo would be cached on `theme` it would never
 * correct itself: the chart ends up permanently one toggle behind.
 *
 * Deferring the read to the next frame puts it after the whole passive-effect
 * flush, including ThemeProvider's, so the values always match what the user
 * is looking at. (Flagged to the Mayor: the same trap applies to every panel
 * that follows chartTheme.ts's `useMemo` guidance. The shared-layer fix is
 * for ThemeProvider to toggle the class in `useLayoutEffect`.)
 */
function useChartColors(): ChartColors {
  const { theme } = useTheme();
  const [colors, setColors] = useState<ChartColors>(chartColors);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setColors(chartColors()));
    return () => window.cancelAnimationFrame(id);
  }, [theme]);

  return colors;
}

function LegendSwatch({ color, children }: { color: string; children: string }) {
  return (
    <span className="flex min-w-0 items-center gap-1">
      <span
        className="inline-block h-[2px] w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span className="truncate">{children}</span>
    </span>
  );
}

export interface PowerLawChartProps {
  readonly history: readonly PricePoint[];
  readonly historyFailed: boolean;
  readonly spot: number | null;
  /** Optional pre-calculated values; overrides only the legend figures. */
  readonly newhedge: NewhedgePowerLaw | null;
}

/**
 * The two model lines are computed from the clock, NOT from the fetched
 * series, so "chart renders with at least two visible lines" holds with the
 * network entirely unplugged. History, when present, is drawn behind them.
 *
 * Log Y axis with decade ticks: a power law is a straight line in log space,
 * and on a linear axis 2011's $0.30 and today's $85,000 cannot share a 175px
 * tall chart at all.
 */
export function PowerLawChart({ history, historyFailed, spot, newhedge }: PowerLawChartProps) {
  const colors = useChartColors();

  // Date.now() lives inside the memo: read at render time it would change on
  // every render and defeat memoisation for no visible benefit.
  const data = useMemo(
    () => buildPowerLawChart({ history, spot, nowMs: Date.now() }),
    [history, spot],
  );

  const endMs = data[data.length - 1]?.t ?? Date.now();
  const domain = useMemo(() => chartDomain(data), [data]);
  const yTicks = useMemo(() => logDecadeTicks(domain[0], domain[1]), [domain]);
  const xTicks = useMemo(() => yearTicks(POWER_LAW_START_MS, endMs), [endMs]);

  const today = powerLawAt(endMs);
  const fairNow = newhedge?.fair ?? today.fair;
  const floorNow = newhedge?.floor ?? today.floor;

  return (
    <>
      {/*
        Inline legend rather than Recharts' <Legend>: that component reserves
        a fixed block of the chart's own height (~30px of 175), which this
        card cannot spare.
      */}
      <div className="flex shrink-0 items-center gap-2.5 text-[9px] font-medium text-ink-subtle">
        <LegendSwatch color={colors.price}>Price</LegendSwatch>
        <LegendSwatch color={colors.powerLaw}>{`Model ${formatUsdCompact(fairNow)}`}</LegendSwatch>
        <LegendSwatch color={colors.floor}>{`Floor ${formatUsdCompact(floorNow)}`}</LegendSwatch>
        {newhedge !== null && <span className="text-accent">newhedge</span>}
        {historyFailed && (
          <span className="ml-auto truncate text-warn" title="Historical price series unavailable">
            history unavailable
          </span>
        )}
      </div>

      {/* ChartArea is mandatory: ResponsiveContainer measures its parent and
          sets its own height, which inside a flex column grows the card until
          the page scrolls. */}
      <ChartArea>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={xTicks}
              tickFormatter={(v: number) => formatYear(v)}
              tick={{ fontSize: 9, fill: colors.axis }}
              tickLine={false}
              axisLine={{ stroke: colors.grid }}
              height={14}
              minTickGap={12}
            />
            <YAxis
              scale="log"
              domain={domain}
              ticks={yTicks}
              tickFormatter={(v: number) => formatUsdCompact(v)}
              tick={{ fontSize: 9, fill: colors.axis }}
              tickLine={false}
              axisLine={false}
              width={34}
            />
            <Tooltip
              content={<PowerLawTooltip colors={colors} />}
              cursor={{ stroke: colors.grid, strokeWidth: 1 }}
              isAnimationActive={false}
            />
            {/* Price first so the two model lines draw on top of it. */}
            <Line
              type="monotone"
              dataKey="price"
              name="Price"
              stroke={colors.price}
              strokeWidth={1.25}
              dot={false}
              activeDot={{ r: 2.5 }}
              connectNulls={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="fair"
              name="Model"
              stroke={colors.powerLaw}
              strokeWidth={1.5}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="floor"
              name="Floor"
              stroke={colors.floor}
              strokeWidth={1.25}
              strokeDasharray="3 3"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartArea>
    </>
  );
}
